import { useEffect, useRef } from 'react';
import {
  ALL_TABS,
  NON_JPEQ_TABS,
  SYMBOL_DETAIL_TABS
} from '../../constants/symbolDetailsTabs';
import useLocalize from '../../utils/hooks/useLocalize';
import useSymbolMetadata from '../../utils/hooks/useSymbolMetadata';
import { isJpeq } from '../../utils/symbol';
import HistoricalTabComponent from './HistoricalTabComponent/HistoricalTabComponent';
import IndicatorsTabComponent from './IndicatorsTabComponent/IndicatorsTabComponent';
import NewsTabComponent from './NewsTabComponent/NewsTabComponent';
import SummaryTabComponent from './SummaryTabComponent/SummaryTabComponent';
import './TabComponent.scss';

const TAB_KEYBOARD_KEYS = {
  ARROW_RIGHT: 'ArrowRight',
  ARROW_LEFT: 'ArrowLeft',
  HOME: 'Home',
  END: 'End'
};

const getTabButtonId = tab => `tab-button-${tab}`;
const getTabPanelId = tab => `tab-panel-${tab}`;

export const TabComponent = ({ mode, setMode, symbol }) => {
  const localize = useLocalize();
  const symbolMetadata = useSymbolMetadata(symbol);
  const isJpeqSymbol = isJpeq(symbolMetadata.assetClass);
  const tabRefs = useRef(new Map());

  const tabs = isJpeqSymbol ? ALL_TABS : NON_JPEQ_TABS;

  // Reset mode when current mode is not available
  useEffect(() => {
    if (!tabs.includes(mode)) {
      setMode(tabs[0]);
    }
  }, [mode, tabs, setMode]);

  const focusTab = tab => {
    const tabButton = tabRefs.current.get(tab);

    if (tabButton) {
      tabButton.focus();
    }
  };

  const setTabRef = tab => (element) => {
    if (element) {
      tabRefs.current.set(tab, element);
      return;
    }

    tabRefs.current.delete(tab);
  };

  const handleTabKeyDown = (event, tab) => {
    const currentIndex = tabs.indexOf(tab);
    let nextIndex = currentIndex;

    switch (event.key) {
      case TAB_KEYBOARD_KEYS.ARROW_RIGHT:
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case TAB_KEYBOARD_KEYS.ARROW_LEFT:
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case TAB_KEYBOARD_KEYS.HOME:
        nextIndex = 0;
        break;
      case TAB_KEYBOARD_KEYS.END:
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    if (nextIndex !== currentIndex) {
      setMode(tabs[nextIndex]);
      focusTab(tabs[nextIndex]);
    }
  };

  const renderTabContent = tab => {
    switch (tab) {
      case SYMBOL_DETAIL_TABS.SUMMARY:
        return <SummaryTabComponent symbol={symbol} />;
      case SYMBOL_DETAIL_TABS.INDICATORS:
        return <IndicatorsTabComponent symbol={symbol} />;
      case SYMBOL_DETAIL_TABS.CHART:
        return 'Chart content goes here.';
      case SYMBOL_DETAIL_TABS.NEWS:
        return <NewsTabComponent symbol={symbol} newsPerPage={25} />;
      case SYMBOL_DETAIL_TABS.HISTORICAL:
        return <HistoricalTabComponent symbol={symbol} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="tab-container" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab}
            id={getTabButtonId(tab)}
            ref={setTabRef(tab)}
            type="button"
            onClick={() => setMode(tab)}
            onKeyDown={(event) => handleTabKeyDown(event, tab)}
            className={`tab-button${mode === tab ? ' active' : ''}`}
            data-testid={`tab-button-${tab}`}
            role="tab"
            tabIndex={mode === tab ? 0 : -1}
            aria-selected={mode === tab}
            aria-controls={getTabPanelId(tab)}
          >
            {localize(`symbol.details.tab.${tab}`)}
          </button>
        ))}
      </div>
      <div>
        {tabs.map((tab) => (
          <div
            key={tab}
            id={getTabPanelId(tab)}
            role="tabpanel"
            aria-labelledby={getTabButtonId(tab)}
            data-testid={`tab-content-${tab}`}
            hidden={mode !== tab}
          >
            {mode === tab && renderTabContent(tab)}
          </div>
        ))}
      </div>
    </div>
  );
};
